import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ListScreenPaginationBar from '../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import {
  faWaveSquare,
  faUsers,
  faCapsules,
  faLink,
  faHeart,
  faStethoscope,
  faSync,
  faPlus,
  faSearch,
  faChevronDown,
  faCheck,
  faTimes,
  faHeartbeat, faUserMd, faWheelchair, faAmbulance,
  faHospital, faSyringe, faPills, faBriefcaseMedical, faNotesMedical,
  faPrescriptionBottleAlt, faLungs, faBrain, faTooth, faBone,
  faVirus, faVial, faDna, faMicroscope, faXRay, faThermometerHalf,
  faLaptopMedical, faMobileAlt, faPhoneAlt, faClinicMedical
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import Table from '../../components/Common/Table/Table';
import ActionButtons from '../../components/Common/ActionButtons/ActionButtons';
import careProgramService from '../../services/careProgramService';
import { getFontAwesomeProgramIcon, getProgramTagStyle } from '../../utils/careProgramUtils';
import ConfirmDeleteModal from '../../components/Common/ConfirmDeleteModal/ConfirmDeleteModal';
import './CarePrograms.css';
import '../PracticeManagement/SystemCaregiverManagement/SystemCaregiverManagement.css';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import { matchesWordStartAny } from '../../utils/searchMatch';

const CarePrograms = () => {
  const navigate = useNavigate();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [searchTerm, setSearchTerm] = useState('');
  const [carePrograms, setCarePrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, perPage: 10 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [isDeletingProgram, setIsDeletingProgram] = useState(false);

  const fetchCarePrograms = async () => {
    setIsLoading(true);
    try {
      const response = await careProgramService.getCarePrograms();
      if (response.success) {
        setCarePrograms(response.data);
      }
    } catch (error) {
      console.error('Error fetching care programs:', error);
      toast.error('Failed to fetch care programs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCarePrograms();
  }, []);

  React.useEffect(() => {
    updateBreadcrumbs([
      { label: 'Care Programs', path: '/care-programs', active: true }
    ]);
    return () => clearBreadcrumbs();
  }, [updateBreadcrumbs, clearBreadcrumbs]);

  const filteredData = useMemo(() => {
    return carePrograms.filter((prog) =>
      matchesWordStartAny([prog.full_name, prog.short_name], searchTerm)
    );
  }, [searchTerm, carePrograms]);

  const paginatedData = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.perPage;
    return filteredData.slice(start, start + pagination.perPage);
  }, [filteredData, pagination]);

  const totalPages = Math.ceil(filteredData.length / pagination.perPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleEntriesChange = (val) => {
    setPagination({ currentPage: 1, perPage: val === 'all' ? 9999 : parseInt(val, 10) });
  };

  const handleDelete = (prog) => {
    setProgramToDelete(prog);
    setShowDeleteModal(true);
  };

  const cancelDeleteProgram = () => {
    if (isDeletingProgram) return;
    setShowDeleteModal(false);
    setProgramToDelete(null);
  };

  const confirmDeleteProgram = async () => {
    if (!programToDelete?.id) return;
    setIsDeletingProgram(true);
    try {
      await careProgramService.deleteCareProgram(programToDelete.id);
      toast.success('Care program deleted successfully');
      setShowDeleteModal(false);
      setProgramToDelete(null);
      fetchCarePrograms();
    } catch (error) {
      console.error('Error deleting care program:', error);
      toast.error('Failed to delete care program');
    } finally {
      setIsDeletingProgram(false);
    }
  };

  const getProgramIcon = (prog) => getFontAwesomeProgramIcon(prog);

  const columns = [
    {
      key: 'index',
      title: '#',
      className: 'reusable-table-centered-col',
      render: (_, rowIndex) => (
        <span>{(pagination.currentPage - 1) * pagination.perPage + rowIndex + 1}</span>
      )
    },
    {
      key: 'name',
      title: 'PROGRAM NAME',
      className: 'cp-left-align-col',
      render: (prog) => (
        <div className="cp-name-cell">
          <FontAwesomeIcon icon={getProgramIcon(prog)} className="cp-program-icon" style={{ color: prog.badge_color }} />
          <span className="cp-program-name">{prog.full_name}</span>
          <span className="cp-program-tag" style={{ backgroundColor: (prog.badge_color || '#263651') + '25', color: prog.badge_color || '#263651' }}>{prog.short_name}</span>
        </div>
      )
    },
    {
      key: 'billingCodes',
      title: 'BILLING CODES',
      render: (prog) => {
        const codes = typeof prog.cpt_codes === 'string' ? JSON.parse(prog.cpt_codes) : (prog.cpt_codes || []);
        return <span className="cp-codes-text">{codes.join(', ') || 'N/A'}</span>;
      }
    },
    {
      key: 'practices',
      title: 'CATEGORY',
      className: 'text-center',
      render: (prog) => <span className="cp-practices-text">{prog.category}</span>
    },
    {
      key: 'actions',
      title: 'ACTION',
      className: 'reusable-table-centered-col',
      render: (prog) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <ActionButtons
            hideView
            onEdit={() => navigate(`/care-programs/edit/${prog.id}`)}
            onDelete={() => handleDelete(prog)}
          />
        </div>
      )
    }
  ];

  return (
    <div className="cp-page-container">
      <div className="scm-page-header">
        <h2 className="dashboard-title">Care Program List</h2>
      </div>

      <div className="cp-summary-section">
        <div className="cp-summary-header">
          <div>
            <h3 className="cp-summary-title">Care Programs Summary</h3>
            <p className="cp-summary-subtitle">Overview across all care programs</p>
          </div>
        </div>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-value color-programs">{carePrograms.length}</span>
            <span className="summary-label font-semibold">Total Programs</span>
          </div>
          <div className="summary-item">
            <span className="summary-value color-blue">{carePrograms.reduce((acc, p) => acc + (typeof p.cpt_codes === 'string' ? JSON.parse(p.cpt_codes) : (p.cpt_codes || [])).length, 0)}</span>
            <span className="summary-label font-semibold">Total Billing Codes</span>
          </div>
          <div className="summary-item">
            <span className="summary-value color-purple">{[...new Set(carePrograms.map(p => p.category))].length}</span>
            <span className="summary-label font-semibold">Categories</span>
          </div>
        </div>
      </div>

      <div className="cp-toolbar-row">
        <div className="cp-search-wrapper">
          <FontAwesomeIcon icon={faSearch} className="cp-search-icon" />
          <input
            type="text"
            placeholder="Search care programs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="list-screen-add-btn" type="button" onClick={() => navigate('/care-programs/add')}>
          <FontAwesomeIcon icon={faPlus} className="list-screen-add-icon" /> Add Program
        </button>
      </div>

      <div className="cp-table-wrapper practice-tab-table-card">
        <Table
          columns={columns}
          data={paginatedData}
          isLoading={isLoading}
          loadingMode="skeleton"
          emptyMessage="No care programs found"
        />
        {filteredData.length > 0 && (
          <ListScreenPaginationBar
            entriesValue={pagination.perPage === 9999 ? 'all' : String(pagination.perPage)}
            onEntriesChange={handleEntriesChange}
            currentPage={pagination.currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={cancelDeleteProgram}
        onConfirm={confirmDeleteProgram}
        isDeleting={isDeletingProgram}
        message={
          <p>
            Are you sure you want to delete the care program{' '}
            <strong>{programToDelete?.full_name || programToDelete?.short_name || 'this program'}</strong>?
          </p>
        }
      />
    </div>
  );
};

export default CarePrograms;
